import React from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

// Minimal, safe markdown -> HTML. Handles headings, paragraphs, bold, italic,
// blockquotes, lists, links. Everything else is escaped.
function renderMarkdown(md) {
  if (!md) return "";
  const esc = (s) => s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  const lines = md.split(/\r?\n/);
  const out = [];
  let inPara = [];
  let inList = null;   // 'ul' | 'ol'
  let inQuote = false;

  const flushPara = () => {
    if (inPara.length) {
      const text = inline(inPara.join(" "));
      out.push(`<p>${text}</p>`);
      inPara = [];
    }
  };
  const closeList = () => { if (inList) { out.push(`</${inList}>`); inList = null; } };
  const closeQuote = () => { if (inQuote) { out.push("</blockquote>"); inQuote = false; } };

  const inline = (s) => {
    s = esc(s);
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer nofollow">$1</a>');
    s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/\*([^*]+)\*/g, "<em>$1</em>");
    s = s.replace(/_([^_]+)_/g, "<em>$1</em>");
    return s;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const trimmed = line.trim();
    if (!trimmed) { flushPara(); closeList(); closeQuote(); continue; }

    const h = /^(#{1,3})\s+(.*)$/.exec(trimmed);
    if (h) {
      flushPara(); closeList(); closeQuote();
      const level = h[1].length;
      out.push(`<h${level + 1}>${inline(h[2])}</h${level + 1}>`);
      continue;
    }
    if (/^>\s?/.test(trimmed)) {
      flushPara(); closeList();
      if (!inQuote) { out.push("<blockquote>"); inQuote = true; }
      out.push(`<p>${inline(trimmed.replace(/^>\s?/, ""))}</p>`);
      continue;
    }
    const ul = /^[-*]\s+(.*)$/.exec(trimmed);
    const ol = /^\d+\.\s+(.*)$/.exec(trimmed);
    if (ul || ol) {
      flushPara(); closeQuote();
      const want = ul ? "ul" : "ol";
      if (inList !== want) { closeList(); out.push(`<${want}>`); inList = want; }
      out.push(`<li>${inline((ul || ol)[1])}</li>`);
      continue;
    }
    closeList(); closeQuote();
    inPara.push(trimmed);
  }
  flushPara(); closeList(); closeQuote();
  return out.join("\n");
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB",
    { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function EssayRead() {
  const { slug } = useParams();
  const [article, setArticle] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    axios.get(`${API}/articles/${slug}`).then(r => setArticle(r.data))
      .catch(e => setError(e?.response?.status === 404
        ? "That essay doesn't exist or hasn't been published."
        : "Couldn't load this essay."));
  }, [slug]);

  return (
    <div data-testid="public-essay-read" className="essay-read max-w-2xl">
      <div className="mb-6">
        <Link to="/essays" className="link-warm" data-testid="essay-back">
          ← All essays
        </Link>
      </div>

      {error && <p className="prose-warm">{error}</p>}

      {article && (
        <>
          <div className="essay-meta-top mb-6">
            {formatDate(article.published_at || article.created_at)}
            {article.tags?.length > 0 && (
              <span className="essay-tags ml-3">
                {article.tags.map(t => <span key={t} className="essay-tag">{t}</span>)}
              </span>
            )}
          </div>
          <h1 className="display-lg mb-8">{article.title}</h1>
          <div className="essay-body prose-warm"
               dangerouslySetInnerHTML={{ __html: renderMarkdown(article.body_markdown) }}/>
          <div className="essay-footer mt-16 pt-8">
            <p className="muted">
              Written by Elle Stuart · {article.word_count?.toLocaleString()} words
            </p>
          </div>
        </>
      )}
    </div>
  );
}
